"use client";

import { useState } from "react";
import { 
  X, 
  Headphones, 
  Phone, 
  Mail, 
  Building2, 
  Play, 
  Pause, 
  Award, 
  FileText, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  ShieldCheck 
} from "lucide-react";
import { TelecallingLead } from "@/lib/demos/telecallingDb";

interface TelecallingDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: TelecallingLead | null;
}

export function TelecallingDetailModal({
  isOpen,
  onClose,
  lead,
}: TelecallingDetailModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  if (!isOpen || !lead) return null;

  const sym = lead.currency === "USD" ? "$" : "₹";
  const durationMins = Math.floor((lead.call_duration_seconds || 240) / 60);
  const durationSecs = (lead.call_duration_seconds || 240) % 60;
  const durationStr = `${durationMins}m ${durationSecs < 10 ? "0" : ""}${durationSecs}s`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-2xl rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 shadow-2xl shadow-cyan-950/50 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center shrink-0">
            <Headphones className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                {lead.lead_no}
              </span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                {lead.stage}
              </span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">
                {lead.disposition.toUpperCase()}
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mt-1.5">{lead.company_name}</h3>
            <p className="text-xs text-slate-400">
              Key Contact: <strong>{lead.prospect_name}</strong> • Program: {lead.campaign_name}
            </p>
          </div>
        </div>

        {/* Call Recording Simulation Player */}
        <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-cyan-400" />
              Recorded Call Dialogue ({durationStr})
            </span>
            <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-500/10 px-2 py-0.5 rounded">
              QA Audited: {lead.qa_score}%
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-10 h-10 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 flex items-center justify-center shrink-0 shadow-md shadow-cyan-500/20 transition-transform active:scale-95"
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
            </button>

            {/* Mock Audio Waveform */}
            <div className="flex-1 flex items-center gap-1 h-8 px-3 rounded-lg bg-slate-900 border border-slate-800">
              {[18, 35, 60, 40, 80, 50, 90, 70, 45, 65, 85, 30, 75, 95, 40, 60, 85, 45, 65, 30, 55, 75, 40, 20].map((h, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-300 ${
                    isPlaying && i % 3 === 0
                      ? "bg-cyan-400 animate-pulse"
                      : isPlaying
                      ? "bg-sky-500"
                      : "bg-slate-700"
                  }`}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>

            <span className="text-xs font-mono text-slate-400">
              {isPlaying ? "01:14" : "00:00"} / {durationStr}
            </span>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-medium">Sentiment</span>
            <span className="text-sm font-bold text-cyan-400">{lead.call_sentiment}</span>
          </div>
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-medium">QA Score</span>
            <span className="text-sm font-bold text-emerald-400">{lead.qa_score} / 100</span>
          </div>
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-medium">Assigned SDR</span>
            <span className="text-sm font-bold text-white truncate block">{lead.assigned_agent}</span>
          </div>
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-medium">Retainer</span>
            <span className="text-sm font-bold text-white">{sym}{lead.monthly_retainer.toLocaleString()}</span>
          </div>
        </div>

        {/* BANT Qualification Status */}
        <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 mb-6 space-y-2 text-xs">
          <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Award className="w-4 h-4 text-cyan-400" />
            BANT Qualification Verification
          </h4>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex items-center gap-1.5 text-slate-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span><strong>Budget:</strong> Verified outsourced allocation</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span><strong>Authority:</strong> Decision maker sign-off confirmed</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span><strong>Need:</strong> Outbound capacity scaling</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span><strong>Timeline:</strong> Target 14-day deployment</span>
            </div>
          </div>
        </div>

        {/* Contact Details & Operational Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs mb-6">
          <div className="p-3 rounded-xl bg-slate-950/40 border border-slate-800 space-y-1.5">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block">Contact Dossier</span>
            <div className="flex items-center gap-2 text-slate-300">
              <Mail className="w-3.5 h-3.5 text-slate-400" />
              <span>{lead.email}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Phone className="w-3.5 h-3.5 text-slate-400" />
              <span>{lead.phone || "Verified B2B Direct"}</span>
            </div>
          </div>

          <div className="p-3 rounded-xl bg-slate-950/40 border border-slate-800 space-y-1">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block">Caller Notes</span>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {lead.notes || "Standard campaign pod intake logged."}
            </p>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end pt-4 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="py-2 px-5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs transition-colors"
          >
            Close Dossier
          </button>
        </div>

      </div>
    </div>
  );
}
